import Head from "next/head";
import { Button, Group, Stack, rem, Title } from "@mantine/core";
import NumberInputCard from "@components/molecules/NewProductPages/NewBlogPages/NumberInputCard";
import AppshellWrapper from "@components/AppshellWrapper/AppshellWrapper";
import SubNavbar from "@components/SubNavbar/SubNavbar";
import BreadcrumbsLinks from "@components/BreadcrumbsLinks/BreadcrumbsLinks";
import Image from "next/image";
import { useEffect, useState } from "react";
import SocialShare from "@components/molecules/SocialIcons";
import Link from "next/link";
import JsonLdScripts from "@components/atoms/JsonLdScripts";
import DateShareReview from "@components/molecules/DateShareReview";
import BlogCardSection from "@components/molecules/NewProductPages/NewBlogPages/BlogCardSection";
import { generateMetaTags } from "@lib/api/tsxHelpers";
import TurnOffPreviewButton from "@components/atoms/Button/TurnOffPreview";
import { useMediaQuery } from "@mantine/hooks";
import CallChat from "@components/molecules/EthinosHomePage/CallChat";
import ScrollToTop from "@components/molecules/EthinosHomePage/ScrollToTop";
import SidePanel from "@components/atoms/EthinosHomeLeft/SidePanel";
import {
  handleSharesIncrement,
  incrementReadCount,
} from "@lib/strapiFunctions/clientSide";
import { ArticleUrls } from "@components/Data/enums";
import { LeadFormTypes } from "@components/LeadForm/enums";
import useLeadFormStore from "../../stores/leadFormStore";
import CTABanner from "@components/CTABanner/CTABanner";

const singlePages = new Set([
  "health-insurance-articles",
  "health-insurance-plans",
  "health-wellness-articles",
  "tax-saving-articles",
  "disease-articles",
  "govt-scheme-articles",
]);

export function getLeadFormType(pageDirectory: string): LeadFormTypes | undefined {
  const key = (pageDirectory || "").trim().toLowerCase();
  if (singlePages.has(key)) return LeadFormTypes.SINGLE;
  if (key === "travel-insurance-articles") return LeadFormTypes.TRAVEL;
  if (key === "corporate-insurance-articles") return LeadFormTypes.CORPORATE;
  return undefined;
}
const showChatBoatPageTypes = ["Corporate-Insurance-Articles", "Disease-Articles", "Health-Insurance-Articles", "Tax-Saving-Articles", "Travel-Insurance-Articles"]

type HrefLangLink = {
  href: string;
  hreflang: string;
};

export default function LiveWellArticles({
  homePageData,
  isds,
  pageData,
  subNavBarLinks,
  slug,
  domain,
  pageDirectory,
  preview,
  leadFormEnabled = false,
  leadFormData,
  pageType,
  hrefLangTags = []
}) {
  const { setLeadFormData } = useLeadFormStore();
  const isMobile = !useMediaQuery("(min-width: 56.25em)");
  const linksArr = [
    { title: "Home", href: "/" },
    { title: pageData.directoryTitle, href: `/${pageDirectory}.html` },
    {
      title: isMobile
        ? pageData.articleHeading.slice(0, 20) + "..."
        : pageData.articleHeading,
      href: `/${pageDirectory}/${slug}`,
    },
  ];

  const initialLeadFormType = pageData?.leadFormTypes == undefined ? getLeadFormType(pageDirectory) : pageData?.leadFormTypes;

  useEffect(() => {
    incrementReadCount(setArticleStats, pageData, ArticleUrls.LIVEWELL);
  }, [slug]);

  const [articleStats, setArticleStats] = useState(pageData?.dateShareReview);

  useEffect(() => {
    if (leadFormData) {
      console.log("Setting lead form data:", leadFormData);
      setLeadFormData(leadFormData);
    }
  }, [leadFormData, setLeadFormData]);

  return (
    <>
      <Head>
        {generateMetaTags(
          pageData?.metaTags,
          domain,
          pageDirectory,
          slug,
          pageData?.heroImageLink,
          pageData?.robots
        )}
        {
          hrefLangTags?.map((link: HrefLangLink, index) => (
          <link
            key={index}
            rel="alternate"
            href={link?.href}
            hrefLang={link?.hreflang}
          />
      ))
        }
      </Head>
      <JsonLdScripts jsonLdScripts={pageData?.jsonLdScriptsData} />
      <AppshellWrapper homePageData={homePageData} popularSearches={false} pageType={pageType}>
        {preview && <TurnOffPreviewButton />}
        <Stack>
          <SubNavbar hideEllipse={false} subNavBarLinks={subNavBarLinks} />
          <BreadcrumbsLinks linksArr={linksArr} />
          <div className="flex flex-col-reverse items-start justify-center gap-10 px-3 md:px-10 md:flex-row w-full max-w-[1280px] mx-auto">
            <div className="flex flex-col w-full gap-5">
              <Title className="text-2xl font-semibold md:text-3xl">
                {pageData.articleHeading}
              </Title>
              <DateShareReview
                pageData={pageData}
                articleStats={articleStats}
              />
              <img
                alt={pageData?.heroImageAlt}
                sizes="100vw"
                height={0}
                width={0}
                loading="lazy"
                src={pageData?.heroImageLink}
                className="md:h-[375px] h-[150px] w-full object-cover rounded-xl"
                // priority={true}
              />
              <SocialShare
                url={`${domain}/${pageDirectory}/${slug}`}
                title={pageData?.articleHeading}
                otherHandler={() =>
                  handleSharesIncrement(
                    setArticleStats,
                    pageData,
                    ArticleUrls.LIVEWELL
                  )
                }
              />

              <div
                className="prose max-w-none prose-a:text-blue-600 prose-p:my-0 prose-headings:my-2 marker:text-black marker:text-xl prose-ul:-mt-2"
                dangerouslySetInnerHTML={{ __html: pageData?.articleRichText }}
              ></div>
             <CTABanner 
              customStaticBannerComponentData={pageData?.customStaticBannerComponentData}
              isds={isds}
               />
              <Group justify="space-between">
                {pageData.previousArticleUrl && (
                  <Button
                    href={pageData.previousArticleUrl}
                    component={Link}
                    variant="filled"
                    color="yellow"
                    size="md"
                    radius="md"
                    className="text-xs md:text-base"
                  >
                    Previous Article
                  </Button>
                )}
                {pageData.nextArticleUrl && (
                  <Button
                    href={pageData.nextArticleUrl}
                    component={Link}
                    variant="filled"
                    color="yellow"
                    size="md"
                    radius="md"
                    className="text-xs md:text-base"
                  >
                    Next Article
                  </Button>

                )}
              </Group>
            </div>
            {leadFormEnabled && <div className="min-w-[235px] md:w-[45rem] w-full">
              <NumberInputCard leadFormType={initialLeadFormType} isds={isds} stripText={pageData?.stripText} stripIcon={pageData?.stripIcon} />
            </div>}
          </div>
          <Stack gap="xl" px={rem(40)} className="pb-2">
            <BlogCardSection
              cardsData={pageData.readMoreArticles}
              imagesAlt={pageData?.heroImageAlt}
            />
            <div className="w-full max-w-[1280px] mx-auto">
            <div
              className="prose max-w-none prose-a:text-blue-600 prose-p:my-0 prose-headings:my-2 marker:text-black marker:text-xl"
              dangerouslySetInnerHTML={{ __html: pageData?.searches }}
            ></div>
            </div>
          </Stack>
        </Stack>
        <SidePanel pageType="Articles" />
        <CallChat showCallBtn={true} showChatBtn={showChatBoatPageTypes.includes(pageType) ? true : false} />
        <ScrollToTop />
      </AppshellWrapper>
    </>
  );
}
